import moment from "../moment/moment";

import * as maxPhoneFunctions from 'react-phone-number-input/max'
import * as mobilePhoneFunctions from 'react-phone-number-input/mobile'

import * as regexes from "../regexes";
import { EU_VAT_NUMBER_REGEX } from "@/constants/regex";
import { DATETIME_FORMAT } from "@/constants";


export const changeFormField = (form, name, value) => {

    const field = form[name] ?? {};

    // Handling field type.
    switch (field.type) {
        case 'email':
            value = value.toLowerCase();
            break;
        case 'not_empty_list':
        case 'list':
            const values = new Set(field.value);
            values.has(value) ? values.delete(value) : values.add(value);
            value = Array.from(values);
            break;
    }

    const isFieldNullable = field.type.includes("nullable_");

    // Setting null to every falsy values in nullable field.
    if (isFieldNullable && !value) {
        value = null;
    }

    return {
        ...form,
        [name]: {
            ...field,
            valid: checkFormFieldValidity(value, field.type),
            modified: true,
            value,
        },
    };
};

export const checkFormFieldValidity = (value, type) => {
    let isValid = true;
    let isFacultative = false;
    if (type.includes("_or_nothing")) {
        switch (typeof (value)) {
            case "string":
                isFacultative = value.length === 0;
                break;
            default:
                isFacultative = !value;
        }
        type = type.replace("_or_nothing", "");
    }
    if (type == "string") {
        isValid = typeof value === "string";
    }
    if (type == "positive_number") {

        isValid = value >= 0 && value != null; // wtf, null >= 0 is true
    }
    if (type == "positive_non_zero_number") {
        isValid = value > 0;
    }
    if (type === "email") {
        isValid = regexes.EMAIL_REGEX.test(value)
    }
    if (type === "phone") {
        isValid = maxPhoneFunctions.isValidPhoneNumber(value)
    }
    if (type === "mobile_phone") {
        isValid = mobilePhoneFunctions.isValidPhoneNumber(value)
    }
    if (type === "url") {
        isValid = regexes.URL_REGEX.test(value)
    }
    if (type === "name") {
        isValid = regexes.LIMIT_60_REGEX.test(value)
    }
    // if (type === "name_or_nothing") {
    //     isValid = regexes.LIMIT_60_REGEX.test(value) || value.length === 0
    // }
    if (type === "adults") {
        isValid = value > 0
    }
    if (type === "tables") {
        isValid = value.length > 0
    }
    if (type === "table_number") {
        isValid = value !== ""
    }
    if (type === "table_seats") {
        isValid = value > 0
    }
    if (type === "not_empty_string") {
        isValid = value.trim() !== ""
    }
    if (type === "not_empty_list") {
        isValid = value.length > 0;
    }
    if (type === "minimum_order_amount") {
        isValid = /^[0-9]+(\.[0-9]{1,2})?$/.test(value) && parseFloat(value) >= 1;
    }
    if (type === "delivery_price") {
        isValid = /^[0-9]+(\.[0-9]{1,2})?$/.test(value) && parseFloat(value) >= 0;
    }
    if (type === "delivery_max_distance") {
        isValid = /^\d+$/.test(value) && parseFloat(value) >= 1;
    }
    if (type === "delivery_free_above") {
        isValid = /^[0-9]+(\.[0-9]{1,2})?$/.test(value) && parseFloat(value) >= 0;
    }
    if (type === "price") {
        isValid = /^[0-9]+(\.[0-9]{1,2})?$/.test(value) && parseFloat(value) >= 0;
    }
    if (type === "stock") {
        isValid = /^\d+$/.test(value) && parseFloat(value) >= 0;
    }
    if (type === "date") {
        isValid = value !== null;
    }
    if (type === "time") {
        isValid = regexes.TIME_REGEX.test(value);
    }
    if (type === "duration") {
        const d = moment.duration(value);
        isValid = d.isValid();
    }
    if (type === "positive_duration") {
        const d = moment.duration(value);
        isValid = d.isValid() && d.asSeconds() > 0;
    }
    if (type === "vat_number") {
        isValid = EU_VAT_NUMBER_REGEX.test(value);
    }
    if (type === "validity_period") {
        isValid = value && typeof value === "object" && typeof value.amount === "number" && typeof value.unit === "string";
    }


    return isValid || isFacultative;
};

export const shouldFieldBeRed = (field) => {
    return !checkFormFieldValidity(field.value, field.type) && field.modified
};

export const areFormFieldsValid = (form) => {
    const fields = Object.keys(form);
    for (const field of fields) {
        if (!form[field].valid) {
            return false;
        }
    }
    return true;
};

export const isFormValid = (form) => {
    const fields = Object.keys(form);
    for (const field of fields) {
        if (!checkFormFieldValidity(form[field].value, form[field].type)) {
            return false;
        }
    }
    return true;
};

export const isModifiedFormValid = (form) => {
    return isFormValid(form) && Object.values(form).some(value => value.modified);
};

export const formToObject = form => {
    const object = {};
    for (const key in form) {
        const value = form[key]['value'] ?? null;
        const isEmptyString = value === '';
        object[key] = isEmptyString ? null : value;
    }
    return object;
}

export const initReservationForm = (form, reservation) => {
    const newForm = { ...form };
    newForm.id.value = reservation.id;
    newForm.customer_id.value = reservation.customer_id;
    newForm.first_name.value = reservation.first_name;
    newForm.last_name.value = reservation.last_name;
    newForm.adults.value = reservation.adults;
    newForm.children.value = reservation.children;
    newForm.start_time_date.value = reservation.start_time_date;
    newForm.start_time_time.value = reservation.start_time_time;
    newForm.tables.value = reservation.tables;
    newForm.event.value = reservation.event;
    newForm.custom_event.value = reservation.custom_event;
    newForm.comment.value = reservation.comment;
    newForm.cake.value = reservation.cake;
    newForm.stroller.value = reservation.stroller;
    newForm.highchair.value = reservation.highchair;
    newForm.reduced_mobility.value = reservation.reduced_mobility;
    newForm.terrace.value = reservation.terrace;
    newForm.duration.value = reservation.duration;
    newForm.restaurant_cancel_message.value = reservation.restaurant_cancel_message;
    newForm.email.value = reservation.email;
    newForm.phone = reservation.phone;
    return newForm
};

export const formToReservation = (form) => {
    const { start_time_time, start_time_date, ...restForm } = form ?? {};
    const dateTime = `${start_time_date.value} ${start_time_time.value}`;
    return {
        ...formToObject(restForm),
        start_time: moment(dateTime, DATETIME_FORMAT).format(),
    }
};


// WEBPACK FOOTER //
// ./src/helpers/forms.js